"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.BoundingBoxIntersectorGrouped = exports.BoundingBoxIntersector = void 0;
/**
 * A class that finds intersections between bounding boxes using an efficient sweep line algorithm.
 */
class BoundingBoxIntersector {
  /**
   * A class that finds intersections between bounding boxes using an efficient sweep line algorithm.
   * @param items The items to find intersections between
   * @param getBbox A function that returns the bounding box of an item. This function will be called once for each item.
   * @param options Options for the intersector
   * @param options.ignoreAdjacentCollisions If true, intersections between adjacent items will be ignored
   * @param options.wrapAround If true, the first and last items will be considered adjacent
   */
  constructor(items, getBbox, options) {
    this.options = options !== null && options !== void 0 ? options : {};
    this.items = items;
    this.bboxCache = [];
    for (let item of items) {
      this.bboxCache.push(getBbox(item));
    }
    this.events = [...items.map((item, itemIndex) => {
      return {
        type: 'start',
        coord: this.bboxCache[itemIndex].min.x,
        item,
        itemIndex
      };
    }), ...items.map((item, itemIndex) => {
      return {
        type: 'end',
        coord: this.bboxCache[itemIndex].max.x,
        item,
        itemIndex
      };
    })];
  }

  /**
   * Yields every pair of items whose bounding boxes intersect.
   */
  *getIntersections() {
    // Sort events by x coordinate
    this.events.sort((a, b) => {
      if (a.coord !== b.coord) return a.coord - b.coord;
      if (a.type === 'start' && b.type === 'end') return -1;
      if (a.type === 'end' && b.type === 'start') return 1;
      return 0;
    });
    let activeEvents = [];
    for (let event of this.events) {
      if (event.type === 'start') {
        // Check for intersections with active items
        for (let activeEvent of activeEvents) {
          if (this.bboxCache[event.itemIndex].max.y < this.bboxCache[activeEvent.itemIndex].min.y) continue;
          if (this.bboxCache[event.itemIndex].min.y > this.bboxCache[activeEvent.itemIndex].max.y) continue;
          if (this.options.ignoreAdjacentCollisions) {
            // Ignore intersections between adjacent entities
            let i = event.itemIndex;
            let j = activeEvent.itemIndex;
            const areAdjacent = this.options.wrapAround ? i === (j + 1) % this.items.length || j === (i + 1) % this.items.length : i === j + 1 || j === i + 1;
            if (areAdjacent) {
              continue;
            }
          }

          // These two items intersect
          yield [{
            item: event.item,
            index: event.itemIndex
          }, {
            item: activeEvent.item,
            index: activeEvent.itemIndex
          }];
        }
        // Add item to active list
        activeEvents.push(event);
      } else if (event.type === 'end') {
        // Remove item from active list
        let index = activeEvents.findIndex(e => e.itemIndex === event.itemIndex);
        activeEvents.splice(index, 1);
      }
    }
  }
}
exports.BoundingBoxIntersector = BoundingBoxIntersector;
/**
 * A class that finds intersections between bounding boxes using an efficient sweep line algorithm.
 */
class BoundingBoxIntersectorGrouped {
  /**
   * A class that finds intersections between bounding boxes using an efficient sweep line algorithm.
   * @param groups Groups of items to find intersections between
   * @param getBbox A function that returns the bounding box of an item. This function will be called once for each item.
   * @param options Options for the intersector
   * @param options.ignoreAllCollisionsWithinGroup If true, intersections between items in the same group will be ignored
   * @param options.ignoreAdjacentCollisionsWithinGroup If true, intersections between adjacent items in the same group will be ignored
   * @param options.wrapAround If true, the first and last items in a group will be considered adjacent
   */
  constructor(groups, getBbox, options) {
    this.options = options !== null && options !== void 0 ? options : {};
    this.bboxCache = [];
    for (let group of groups) {
      let groupCache = [];
      for (let item of group) {
        groupCache.push(getBbox(item));
      }
      this.bboxCache.push(groupCache);
    }
    this.events = [...groups.flatMap((items, groupIndex) => items.map((item, itemIndex) => {
      return {
        type: 'start',
        coord: this.bboxCache[groupIndex][itemIndex].min.x,
        item,
        itemIndex,
        group: items,
        groupIndex
      };
    })), ...groups.flatMap((items, groupIndex) => items.map((item, itemIndex) => {
      return {
        type: 'end',
        coord: this.bboxCache[groupIndex][itemIndex].max.x,
        item,
        itemIndex,
        group: items,
        groupIndex
      };
    }))];
  }

  /**
   * Yields every pair of items whose bounding boxes intersect.
   */
  *getIntersections() {
    // Sort events by x coordinate
    this.events.sort((a, b) => {
      if (a.coord !== b.coord) return a.coord - b.coord;
      if (a.type === 'start' && b.type === 'end') return -1;
      if (a.type === 'end' && b.type === 'start') return 1;
      return 0;
    });
    let activeEvents = [];
    for (let event of this.events) {
      if (event.type === 'start') {
        // Check for intersections with active items
        for (let activeEvent of activeEvents) {
          if (this.options.ignoreAllCollisionsWithinGroup && event.groupIndex === activeEvent.groupIndex) {
            // Ignore all intersections between items in the same group
            continue;
          }
          if (this.options.ignoreAdjacentCollisionsWithinGroup && event.groupIndex === activeEvent.groupIndex) {
            // Ignore intersections between adjacent entities
            let i = event.itemIndex;
            let j = activeEvent.itemIndex;
            const areAdjacent = this.options.wrapAround ? i === (j + 1) % event.group.length || j === (i + 1) % event.group.length : i === j + 1 || j === i + 1;
            if (areAdjacent) {
              continue;
            }
          }
          if (this.bboxCache[event.groupIndex][event.itemIndex].max.y < this.bboxCache[activeEvent.groupIndex][activeEvent.itemIndex].min.y) {
            continue;
          }
          if (this.bboxCache[event.groupIndex][event.itemIndex].min.y > this.bboxCache[activeEvent.groupIndex][activeEvent.itemIndex].max.y) {
            continue;
          }

          // These two items intersect
          yield [{
            item: event.item,
            index: event.itemIndex,
            groupIndex: event.groupIndex
          }, {
            item: activeEvent.item,
            index: activeEvent.itemIndex,
            groupIndex: activeEvent.groupIndex
          }];
        }
        // Add item to active list
        activeEvents.push(event);
      } else if (event.type === 'end') {
        // Remove item from active list
        let index = activeEvents.findIndex(e => e.itemIndex === event.itemIndex && e.groupIndex === event.groupIndex);
        activeEvents.splice(index, 1);
      }
    }
  }
}
exports.BoundingBoxIntersectorGrouped = BoundingBoxIntersectorGrouped;